import {
    ApartmentOutlined,
    CheckCircleFilled,
    CompressOutlined,
    DeleteFilled,
    DeleteOutlined,
    ExpandOutlined,
    EyeInvisibleOutlined,
    InfoCircleOutlined,
    MergeCellsOutlined,
    MergeOutlined,
    OneToOneOutlined,
    PieChartOutlined,
    ProfileTwoTone,
    StarFilled,
    StarOutlined,
    WarningFilled,
} from "@ant-design/icons";
import { faCodeMerge } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Dropdown, Flex, InputNumber, Radio, Select, Space, Spin, TableColumnsType, Tooltip, Typography } from "antd";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import React, { useCallback, useEffect, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataTable from "../DataTable";
import DuplicateDataObjectJobMetaData from "../DuplicateDataObjectJobMetaData";
import DuplicateDataObjectListActionButtons from "./DuplicateDataObjectListActionButtons";

const { Text } = Typography;
const { Option } = Select;

interface IDuplicateDataObjectListProps {
    job: IDataObjectsDuplicateJob;
    dataTags: string[];
    dataObjectList: IDataObjectRecord[];
    loadingMore?: boolean;
    selectedGroupId: string | null;
    onPageSelect: (pagination: { current: number; pageSize: number }, jobId: string) => void;
    onDataObjectSelected: (obj: IDataObjectRecord | undefined) => void;
    onShowSummaryClick: (job: IDataObjectsDuplicateJob) => void;
    loadAllRecords?: (id: string) => void;
    onNominateSurvivor: (obj: IDataObjectRecord, type: string) => void;
    onRemoveSurvivorNomination: (obj: IDataObjectRecord) => void;
    onPreviewClick: (obj: IDataObjectRecord) => void;
    onMergeRulesClick: (obj: IDataObjectRecord, group: IDataObjectRecord[]) => void;
    onSelectGroupId: (id: string | null) => void;
}

export default function DuplicateDataObjectList({
    job,
    dataTags,
    dataObjectList,
    selectedGroupId,
    loadingMore,
    onPageSelect,
    onDataObjectSelected,
    onShowSummaryClick,
    loadAllRecords,
    onNominateSurvivor,
    onRemoveSurvivorNomination,
    onPreviewClick,
    onMergeRulesClick,
    onSelectGroupId,
}: IDuplicateDataObjectListProps) {
    const [showJobMetaData, setShowJobMetaData] = useState<boolean>(false);
    const [showExpandedGroup, setShowExpandedGroup] = useState<boolean>(false);
    const [countFilter, setCountFilter] = useState<{ operator: string; value: number }>({ operator: ">", value: 1 });
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [pagination, setPagination] = useState<IPagination>({ current: 1, defaultPageSize: 50, pageSize: 50, page: 1 });
    const [showMenuGuide, setShowMenuGuide] = React.useState<boolean>(false);
    const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");
    const [clustersOnly, setClustersOnly] = useState<boolean>(true);

    React.useEffect(() => {
        setShowMenuGuide(isReady);
    }, [isReady]);

    const onChangeClustersOnly = useCallback(() => {
        const newView = !clustersOnly;
        if (newView) {
            setCountFilter({ operator: ">", value: 1 });
        } else {
            setCountFilter({ operator: ">=", value: 1 });
        }
        setClustersOnly(newView);
    }, [clustersOnly]);

    const getFilteredDataObjectList = useCallback(
        (dataObjectList: IDataObjectRecord[]) => {
            if (dataObjectList && dataObjectList.length > 0) {
                // Get only selected group
                if (selectedGroupId !== null) {
                    return dataObjectList.filter((dataObject) => dataObject._groupId === selectedGroupId);
                }

                let filteredList = structuredClone(dataObjectList);

                // Show collapsed view, show only either the first in the group or the survivor
                if (!showExpandedGroup) {
                    filteredList = filteredList.filter((dataObj) => {
                        if (dataObj._survivor) {
                            // Return survivor as first choice
                            return true;
                        } else if (dataObj._deleteDuplicates || dataObj._mergeDuplicates) {
                            // Do not return those marked to delete or merge
                            return false;
                        }

                        // All other dataset
                        return dataObj._groupSequence === 1;
                    });
                }

                switch (countFilter.operator) {
                    case ">":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize > countFilter.value);
                        break;
                    case "=":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize === countFilter.value);
                        break;
                    case "<":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize < countFilter.value);
                        break;
                    case ">=":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize >= countFilter.value);
                        if (countFilter.value === 1) {
                            setClustersOnly(false); // User changed the filter
                        }
                        break;
                    case "<=":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize <= countFilter.value);
                        break;
                    case "!=":
                        filteredList = filteredList.filter((dataObject) => dataObject._groupSize !== countFilter.value);
                        break;
                    default:
                        break;
                }
                return filteredList;
            }

            return dataObjectList;
        },
        [countFilter.operator, countFilter.value, selectedGroupId, showExpandedGroup],
    );

    const [filteredDataObjectList, setFilteredDataObjectList] = useState<IDataObjectRecord[] | undefined>();
    const [paginatedDataObjectList, setPaginatedDataObjectList] = useState<IDataObjectRecord[] | undefined>();

    useEffect(() => {
        setFilteredDataObjectList(getFilteredDataObjectList(dataObjectList));
    }, [dataObjectList, getFilteredDataObjectList]);

    // This might be wrong
    useEffect(() => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        if (loadAllRecords) {
            loadAllRecords(job.jobId);
        }
    }, [showExpandedGroup, countFilter, selectedGroupId, loadAllRecords, job.jobId]);

    useEffect(() => {
        if (filteredDataObjectList && pagination?.page) {
            setPaginatedDataObjectList(filteredDataObjectList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize));
        }
    }, [filteredDataObjectList, pagination]);

    const onClickRow = (record: IDataObjectRecord) => {
        return {
            onClick: () => {
                setSelectedRowKeys([record._dataObjectId]);
                filteredDataObjectList &&
                    onDataObjectSelected &&
                    onDataObjectSelected(filteredDataObjectList.find((dataObject) => dataObject._dataObjectId === record._dataObjectId));
            },
        };
    };

    const onPageClick = (page: number, pageSize: number) => {
        let currentPagination = { ...pagination };
        let currentPaginatedDataObjectList = filteredDataObjectList && filteredDataObjectList.slice((page - 1) * pageSize, page * pageSize);
        setPaginatedDataObjectList(currentPaginatedDataObjectList);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
        if (currentPaginatedDataObjectList && currentPaginatedDataObjectList.length <= pageSize) {
            onPageSelect && onPageSelect({ current: page, pageSize }, job.jobId);
        }
    };

    const onMergeMenuClick = React.useCallback(
        ({ key, dataObject }: { key: string; dataObject: IDataObjectRecord }) => {
            switch (key) {
                case "PREVIEW_RECORD_MERGE":
                    onPreviewClick(dataObject);
                    break;
                case "MERGE_RULES":
                    onMergeRulesClick(
                        dataObject,
                        dataObjectList.filter((item) => item._groupId === dataObject._groupId),
                    );
                    break;
                default:
                    break;
            }
        },
        [dataObjectList, onMergeRulesClick, onPreviewClick],
    );

    const columns: TableColumnsType<IDataObjectRecord> = React.useMemo(() => {
        const cols: TableColumnsType<IDataObjectRecord> = [
            {
                fixed: "left",
                width: 40,
                dataIndex: "_dataObjectId",
                key: "duplicate-dataobject-menu-buttons",
                className: "overflow-visible !p-1",
                render: (value, dataObject, index) => (
                    <DuplicateDataObjectListActionButtons
                        key={`${value}-${index}`}
                        dataObjectId={value}
                        dataSetId={dataObject._dataSetId}
                        schemaId={dataObject._schemaId}
                        businessAreaId={dataObject._businessAreaId}
                        selected={selectedRowKeys.some((a) => a === value)}
                        children={
                            selectedGroupId === null && !showExpandedGroup && dataObject._groupSize > 1 ? (
                                <Tooltip title="Compare Singletons" placement="right">
                                    <Button
                                        type="link"
                                        icon={<FontAwesomeIcon icon={faCodeMerge} />}
                                        onClick={(e) => {
                                            e && e.stopPropagation && e.stopPropagation();
                                            onSelectGroupId(dataObject._groupId);
                                        }}
                                    />
                                </Tooltip>
                            ) : undefined
                        }
                    />
                ),
            },
            {
                width: 60,
                title: "No.",
                key: "index",
                render: (value, dataObject, index) => {
                    return <Text className="text-nowrap">{dataObject.sNo || index + 1}</Text>;
                },
            },
            {
                width: 100,
                title: "Grouping ID",
                dataIndex: "_groupId",
                key: "_groupId",
                className: "px-2",
                render: (value, dataObject, index) => {
                    let progressFeedback = null;
                    switch (dataObject._nominationResult) {
                        case "IN_PROGRESS":
                            progressFeedback = (
                                <Tooltip title="Nomination save in progress">
                                    <Spin size="small"></Spin>
                                </Tooltip>
                            );
                            break;
                        case "SUCCESS":
                            progressFeedback = (
                                <Tooltip title="Record nomination saved">
                                    <CheckCircleFilled style={{ color: "green" }} />
                                </Tooltip>
                            );
                            break;
                        case "FAILED":
                            progressFeedback = (
                                <Tooltip title="Record nomination failed">
                                    <WarningFilled style={{ color: "red" }} />
                                </Tooltip>
                            );
                            break;
                        case "REMOVE_IN_PROGRESS":
                            progressFeedback = (
                                <Tooltip title="Nomination removal in progress">
                                    <Spin size="small"></Spin>
                                </Tooltip>
                            );
                            break;
                        default:
                            break;
                    }
                    return (
                        <Flex className="justify-between">
                            {dataObject._groupSize > 1 && selectedGroupId === null ? (
                                <Tooltip title="Show singletons in this group" placement="right">
                                    <Button
                                        type="dashed"
                                        size="small"
                                        onClick={(e) => {
                                            e && e.stopPropagation && e.stopPropagation();
                                            onSelectGroupId(dataObject._groupId);
                                        }}
                                        icon={dataObject._groupSize > 1 ? <ApartmentOutlined style={{ color: "orange" }} /> : <OneToOneOutlined style={{ color: "green" }} />}
                                    >
                                        {value}
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Flex className="gap-1">
                                    {dataObject._groupSize > 1 ? <ApartmentOutlined style={{ color: "orange" }} /> : <OneToOneOutlined style={{ color: "green" }} />}
                                    {value}
                                </Flex>
                            )}
                            {progressFeedback}
                            {dataObject._survivor && (
                                <Tooltip placement="bottomLeft" title="Surviving record">
                                    <StarFilled className="text-yellow-500" />
                                </Tooltip>
                            )}
                            {dataObject._deleteDuplicates && (
                                <Tooltip placement="bottomLeft" title="Record to be deleted">
                                    <DeleteFilled className="text-red-700" />
                                </Tooltip>
                            )}
                            {dataObject._mergeDuplicates && (
                                <Tooltip placement="bottomLeft" title="Record to be merged">
                                    <MergeCellsOutlined className="text-blue-700" />
                                </Tooltip>
                            )}
                        </Flex>
                    );
                },
            },
            {
                width: 140,
                title: "Duplication Option",
                dataIndex: "_deduplicationOption",
                key: "_deduplicationOption",
                ellipsis: true,
                render: (value, dataObject, index) => {
                    return (
                        <Space>
                            {dataObject._groupSize > 1 &&
                                (dataObject._survivor ? (
                                    <Tooltip title="Surviving record. Click to remove nomination">
                                        <Button
                                            icon={dataObject._deduplicationOption === "DELETE_DUPLICATES" ? <DeleteFilled /> : <StarFilled />}
                                            size="small"
                                            type="primary"
                                            style={{ background: dataObject._deduplicationOption === "DELETE_DUPLICATES" ? "red" : "green" }}
                                            disabled={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                            onClick={(e) => {
                                                e.stopPropagation && e.stopPropagation();
                                                onRemoveSurvivorNomination(dataObject);
                                            }}
                                            loading={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                        ></Button>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Nominate surviving record">
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        label: "Delete duplicates",
                                                        key: "NOMINATE_DELETE_DUPLICATES",
                                                        disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                        icon: <DeleteOutlined />,
                                                        onClick: (info) => {
                                                            info.domEvent.stopPropagation && info.domEvent.stopPropagation();
                                                            info.domEvent.preventDefault && info.domEvent.preventDefault();
                                                            onNominateSurvivor(dataObject, "DELETE_DUPLICATES");
                                                        },
                                                    },
                                                    {
                                                        label: "Merge duplicates",
                                                        key: "NOMINATE_MERGE_DUPLICATES",
                                                        disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                        icon: <MergeCellsOutlined />,
                                                        onClick: (info) => {
                                                            info.domEvent.stopPropagation && info.domEvent.stopPropagation();
                                                            info.domEvent.preventDefault && info.domEvent.preventDefault();
                                                            onNominateSurvivor(dataObject, "MERGE_DUPLICATES");
                                                        },
                                                    },
                                                ],
                                            }}
                                        >
                                            <Button
                                                icon={<StarOutlined />}
                                                size="small"
                                                type="primary"
                                                className="bg-gray-400"
                                                disabled={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                loading={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                onClick={(e) => {
                                                    e.stopPropagation && e.stopPropagation();
                                                    e.preventDefault && e.preventDefault();
                                                }}
                                            ></Button>
                                        </Dropdown>
                                    </Tooltip>
                                ))}
                            {value && dataObject._survivor && dataObject._deduplicationOption === "MERGE_DUPLICATES" ? (
                                <Dropdown
                                    menu={{
                                        onClick: (e) => {
                                            e.domEvent && e.domEvent.stopPropagation && e.domEvent.stopPropagation();
                                            onMergeMenuClick({ key: e.key, dataObject });
                                        },
                                        items: [
                                            {
                                                label: "Preview record merge",
                                                key: "PREVIEW_RECORD_MERGE",
                                                disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                icon: (
                                                    <ProfileTwoTone
                                                        disabled={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                        style={{ color: "yellow", cursor: "pointer" }}
                                                    />
                                                ),
                                            },
                                            {
                                                label: "Merge rules",
                                                key: "MERGE_RULES",
                                                disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                icon: <MergeCellsOutlined />,
                                            },
                                        ],
                                    }}
                                >
                                    <Button
                                        icon={<MergeOutlined />}
                                        type="dashed"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation && e.stopPropagation();
                                            e.preventDefault && e.preventDefault();
                                        }}
                                    >
                                        Merge Rules
                                    </Button>
                                </Dropdown>
                            ) : (
                                value && <Text type="danger">{dataObject._deduplicationOption === "DELETE_DUPLICATES" ? "Delete duplicates" : "Merge duplicates"}</Text>
                            )}
                        </Space>
                    );
                },
            },
            {
                width: 100,
                title: "Count",
                dataIndex: "_groupSize",
                key: "_groupSize",
                render: (value, dataObject, _i) => {
                    return <Text strong>{showExpandedGroup || selectedGroupId !== null ? `${dataObject._groupSequence}/${value}` : value}</Text>;
                },
            },
            {
                width: 120,
                title: "Business Area",
                dataIndex: "_businessAreaName",
                key: "_businessAreaName",
                className: "text-nowrap",
            },
            {
                width: 100,
                title: "Schema",
                dataIndex: "_schemaName",
                key: "_schemaName",
                className: "text-nowrap",
            },
            {
                width: 100,
                title: "Dataset",
                dataIndex: "_dataSetName",
                key: "_dataSetName",
                className: "text-nowrap",
            },
        ];

        if (dataTags) {
            dataTags.forEach((tag, index) => {
                cols.push({
                    key: index,
                    dataIndex: tag,
                    title: tag,
                    width: tag.length * 14 + 100,
                    render: (value, dataObject, index) => {
                        if (dataObject._tags && dataObject._tags[tag] && dataObject._tags[tag].length > 0) {
                            return (
                                <div className="inline-flex flex-col gap-1">
                                    {dataObject._tags[tag].map((field) => {
                                        return (
                                            <div className="inline-flex flex-wrap items-center gap-1" key={field.fieldId}>
                                                {(showExpandedGroup || !clustersOnly) && (
                                                    <span className="text-nowrap rounded-sm border border-slate-200 bg-slate-100 px-1 text-xs text-gray-600">{field.name}:</span>
                                                )}
                                                <span className="text-nowrap">{field.value}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        }
                        return value;
                    },
                });
            });
        }

        return cols;
    }, [clustersOnly, dataTags, onMergeMenuClick, onNominateSurvivor, onRemoveSurvivorNomination, onSelectGroupId, selectedGroupId, selectedRowKeys, showExpandedGroup]);

    return (
        <DataTable<IDataObjectRecord>
            data-testid={CypressTestIds.DUPLICATE_DATAOBJECT_CONTAINER} // For the container
            header={
                !selectedGroupId && (
                    <div className="flex w-full flex-col gap-3 rounded-md border border-slate-100 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center justify-start gap-4">
                            <Radio.Group className="inline-flex flex-nowrap" value={showExpandedGroup} onChange={(e) => setShowExpandedGroup(e.target.value)}>
                                <Tooltip placement="topLeft" title="Toggle to expand groupings.">
                                    <Radio.Button value={true}>
                                        <ExpandOutlined style={{ marginRight: "6px" }} />
                                        <span>Expanded</span>
                                    </Radio.Button>
                                </Tooltip>
                                <Tooltip placement="topLeft" title="Toggle to collapse groupings.">
                                    <Radio.Button value={false}>
                                        <CompressOutlined style={{ marginRight: "6px" }} />
                                        <span>Collapsed</span>
                                    </Radio.Button>
                                </Tooltip>
                            </Radio.Group>
                            <Checkbox checked={clustersOnly} onChange={onChangeClustersOnly} className="text-nowrap">
                                View Clusters Only
                            </Checkbox>
                            <div className="flex flex-nowrap items-center gap-2">
                                <Text className="text-nowrap">Count</Text>
                                <InputNumber
                                    disabled={loadingMore}
                                    style={{ width: "8rem", verticalAlign: "middle" }}
                                    min={1}
                                    max={1000000}
                                    defaultValue={countFilter.value}
                                    value={countFilter.value}
                                    onChange={(value) => value && setCountFilter({ ...countFilter, value })}
                                    addonBefore={
                                        <Select
                                            defaultValue={countFilter.operator}
                                            value={countFilter.operator}
                                            onChange={(value) => setCountFilter({ ...countFilter, operator: value })}
                                        >
                                            <Option value=">">{">"}</Option>
                                            <Option value="=">{"="}</Option>
                                            <Option value="<">{"<"}</Option>
                                            <Option value=">=">{">="}</Option>
                                            <Option value="<=">{"<="}</Option>
                                            <Option value="!=">{"!="}</Option>
                                        </Select>
                                    }
                                />
                            </div>
                            <div className="flex items-center">{loadingMore ? <Spin className="inline-spin-text" tip="Getting all matching records..."></Spin> : <></>}</div>
                            <div className="flex flex-1 justify-end gap-2">
                                <Button type="dashed" onClick={() => onShowSummaryClick(job)} icon={<PieChartOutlined />}>
                                    Show Job Summary
                                </Button>
                                <Tooltip placement="topRight" title={showJobMetaData ? "Hide job metadata" : "View job metadata"}>
                                    <Button
                                        type="dashed"
                                        onClick={() => setShowJobMetaData((prev) => !prev)}
                                        icon={showJobMetaData ? <EyeInvisibleOutlined /> : <InfoCircleOutlined />}
                                    >
                                        Job Metadata
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                        <DuplicateDataObjectJobMetaData job={job} show={showJobMetaData} />
                    </div>
                )
            }
            resizable
            hideSelectionColumn
            dataTestId={CypressTestIds.DUPLICATE_DATAOBJECT_TABLE_LIST} // For the table
            tableProps={{
                dataSource: paginatedDataObjectList,
                columns: columns,
                rowKey: "_dataObjectId",
                className: "duplciate-dataobject-list",
                rowClassName: "cursor-pointer",
                onRow: onClickRow,
                onChange: (pagination, _filters, _sorter, extra) => extra.action === "paginate" && onPageClick && onPageClick(pagination.current ?? 1, pagination.pageSize ?? 100),
                rowSelection: {
                    type: "radio",
                    selectedRowKeys,
                    onChange: (selected) => setSelectedRowKeys(selected),
                },
                pagination: {
                    total: filteredDataObjectList?.length ?? 0,
                    defaultPageSize: pagination.defaultPageSize,
                    pageSize: pagination.pageSize,
                },
            }}
            emptyText={clustersOnly && paginatedDataObjectList?.length === 0 ? "No cluster found" : "No record founc"}
        >
            {paginatedDataObjectList && paginatedDataObjectList.length > 0 && (
                <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} zIndex={dataTags?.length ?? 0} />
            )}
        </DataTable>
    );
}
